import PropTypes from "prop-types";
import { useContext, useEffect, useMemo } from "react";
// MUI
import { gql, useLazyQuery } from "@apollo/client";
import {
  Card,
  Collapse,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Skeleton,
  Tooltip,
} from "@mui/material";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import dayjs from "dayjs";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "50%", xxl: "40%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalCuestionarioReporte({ open, handleClose, selectedCuestionario }) {
  const { handleSnackbar } = useContext(MessageManager);

  const [getCuestionario, { data, loading: loadingCuestionario }] = useLazyQuery(
    gql`
      query getCuestionario($id: ID!) {
        cuestionario(id: $id) {
          id
          nombre
          descripcion
          fechaLimite
          obligatorio
          preguntas {
            id
            titulo
            tipo
            opciones
          }
          respuestas
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (selectedCuestionario && open) {
      getCuestionario({
        variables: {
          id: selectedCuestionario?.id,
        },
      });
    }
  }, [selectedCuestionario, open]);

  // Agrupamos las respuestas por idPregunta
  const respuestasAgrupadas = useMemo(() => {
    if (!data?.cuestionario?.respuestas) return {};
    return data.cuestionario.respuestas.reduce((acc, alumno) => {
      alumno.respuestas.forEach((resp) => {
        const key = resp.idPregunta;
        if (!acc[key]) acc[key] = [];
        acc[key].push({
          alumno: alumno.usuario,
          respuesta: resp.respuesta,
        });
      });
      return acc;
    }, {});
  }, [data]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">Reportes del cuestionario</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InputLabel htmlFor="nombre">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nombre
                    </SoftTypography>
                  </InputLabel>
                  {loadingCuestionario ? (
                    <Skeleton variant="text" width="100%" height={40} />
                  ) : (
                    <SoftBox display="flex" gap={1} alignItems="center">
                      <SoftTypography variant="h6">
                        {data?.cuestionario?.nombre || ""}
                      </SoftTypography>
                      {data?.cuestionario?.obligatorio && (
                        <SoftBadge color="primary" badgeContent="Obligatorio" />
                      )}
                      {dayjs(data?.cuestionario?.fechaLimite).isBefore(dayjs()) ? (
                        <SoftBadge color="uproYellow" badgeContent="Vencido" />
                      ) : (
                        <SoftBadge
                          color="uproGreen"
                          badgeContent={`Vence en ${dayjs(data?.cuestionario?.fechaLimite).diff(
                            dayjs(),
                            "days"
                          )} días`}
                        />
                      )}
                    </SoftBox>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <InputLabel htmlFor="descripcion">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Descripción
                    </SoftTypography>
                  </InputLabel>
                  {loadingCuestionario ? (
                    <Skeleton variant="text" width="100%" height={40} />
                  ) : (
                    <SoftTypography variant="h6">
                      {data?.cuestionario?.descripcion || ""}
                    </SoftTypography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <SoftBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
                    <SoftTypography variant="h6">Respuestas</SoftTypography>
                    <SoftBadge
                      color={data?.cuestionario?.respuestas?.length === 0 ? "primary" : "uproGreen"}
                      badgeContent={
                        loadingCuestionario
                          ? "Cargando..."
                          : `${data?.cuestionario?.respuestas?.length || 0} Respuestas`
                      }
                    />
                  </SoftBox>
                  <Collapse in={!loadingCuestionario}>
                    <SoftBox display="flex" flexDirection="column" gap={2}>
                      {data?.cuestionario?.preguntas?.length > 0 ? (
                        data.cuestionario.preguntas.map((pregunta, index) => {
                          // Obtenemos las respuestas de esta pregunta del objeto agrupado.
                          const respuestasParaPregunta = respuestasAgrupadas[pregunta.id] || [];

                          // Agrupamos y contamos las respuestas únicas
                          const conteoRespuestas = respuestasParaPregunta.reduce((acc, resp) => {
                            const key = resp.respuesta;
                            acc[key] = (acc[key] || 0) + 1;
                            return acc;
                          }, {});

                          const maxCantidad = Math.max(...Object.values(conteoRespuestas));

                          return (
                            <SoftBox key={pregunta.id}>
                              <Card>
                                <SoftBox p={2}>
                                  <SoftTypography variant="h6" fontWeight="bold">
                                    {index + 1}. {pregunta.titulo}
                                  </SoftTypography>
                                  <SoftBox display="flex" flexDirection="column" gap={1} mt={1}>
                                    {Object.keys(conteoRespuestas).length > 0 ? (
                                      Object.entries(conteoRespuestas)
                                        .sort(([, a], [, b]) => b - a)
                                        .map(([respuesta, cantidad], i) => (
                                          <SoftBox
                                            key={i}
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                          >
                                            <SoftButton
                                              color={
                                                i === 0 || cantidad === maxCantidad
                                                  ? "uproGreen"
                                                  : i === 1 && cantidad > 0
                                                  ? "uproYellow"
                                                  : "primary"
                                              }
                                              iconOnly
                                              size="small"
                                              circular
                                            >
                                              {cantidad}
                                            </SoftButton>
                                            <SoftTypography variant="h7" fontWeight="regular">
                                              {respuesta}
                                            </SoftTypography>
                                          </SoftBox>
                                        ))
                                    ) : (
                                      <SoftTypography variant="body2" color="text.secondary">
                                        Sin respuestas
                                      </SoftTypography>
                                    )}
                                  </SoftBox>
                                </SoftBox>
                              </Card>
                            </SoftBox>
                          );
                        })
                      ) : (
                        <SinDatos />
                      )}
                    </SoftBox>
                  </Collapse>
                  <Collapse in={loadingCuestionario}>
                    <Loading />
                  </Collapse>
                </Grid>
              </Grid>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalCuestionarioReporte.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  selectedCuestionario: PropTypes.object,
};
